/**
 * Contenuti legali/informativi condivisi della piattaforma TL4K.
 *
 * Usati sia dalle pagine dedicate di TechyWeb (rotta /privacy, /accessibilita,
 * /open-source, /azienda) sia dal footer standard (`TL4KAppFooter`) di tutte le
 * app standalone tramite il viewer a schermo intero.
 *
 * Stile: design token `--tl4k-*` con fallback, così da degradare correttamente
 * anche nelle app che non montano `ThemeProvider`.
 */

/** Data di aggiornamento dei contenuti legali (aggiornare a ogni modifica sostanziale). */
export const LEGAL_PAGES_UPDATED_AT = "10/08/2026";

const cardStyle: React.CSSProperties = {
  backgroundColor: "var(--tl4k-surface-default, #ffffff)",
  border: "1px solid var(--tl4k-neutral-200, #eeeeee)",
  borderRadius: "var(--tl4k-border-radius-lg, 16px)",
  boxShadow: "var(--tl4k-shadow-sm, 0 1px 3px rgba(0,0,0,0.06))",
  padding: "var(--tl4k-spacing-lg, 16px) var(--tl4k-spacing-xl, 24px)",
  margin: 0,
};

const headingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--tl4k-font-size-subheading, 22px)",
  fontFamily: "var(--tl4k-font-family-headline, inherit)",
  color: "var(--tl4k-neutral-800, #3a3939)",
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--tl4k-font-size-body, 20px)",
  lineHeight: 1.6,
};

const linkStyle: React.CSSProperties = {
  color: "var(--tl4k-primary-700, #005a80)",
  textDecoration: "underline",
};

const noteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--tl4k-font-size-small, 18px)",
  color: "var(--tl4k-neutral-600, #6b6a6a)",
};

/** Card informativa riutilizzabile. */
function LegalCard({
  title,
  children,
  style,
}: {
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <section className="tl4k-legal-content" style={{ ...cardStyle, ...style }}>
      <h2 style={headingStyle}>{title}</h2>
      {children}
    </section>
  );
}

/** Riga etichetta/valore per i dati societari. */
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--tl4k-spacing-xs, 4px)",
        padding: "var(--tl4k-spacing-md, 12px) 0",
        borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)",
      }}
    >
      <dt
        style={{
          margin: 0,
          fontSize: "var(--tl4k-font-size-small, 18px)",
          fontWeight: 600,
          color: "var(--tl4k-primary-700, #005a80)",
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, fontSize: "var(--tl4k-font-size-body, 20px)" }}>{value}</dd>
    </div>
  );
}

/* ─────────────────────────── La Società ─────────────────────────── */

export function CompanyLegalContent() {
  return (
    <>
      <p style={bodyStyle}>
        TechLAB4Kids è un&apos;<strong>impresa sociale</strong> ai sensi del{" "}
        <a
          href="https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:2017-07-03;112"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          D.Lgs. 3 luglio 2017, n. 112
        </a>{" "}
        e s.m.i., che sviluppa strumenti didattici digitali per bambine e bambini.
      </p>

      <LegalCard title="Identificazione">
        <dl style={{ margin: 0 }}>
          <InfoRow label="Denominazione" value="TechLAB4Kids S.r.l. Impresa Sociale" />
          <InfoRow
            label="Sede legale"
            value="Via Rinaldo Loi, 1 — 09013 Cortoghiana (SU)"
          />
          <InfoRow label="Partita IVA" value="04084980921" />
          <InfoRow label="Codice fiscale" value="90043090928" />
          <InfoRow
            label="E-mail"
            value={
              <a href="mailto:info@techlab4kids.it" style={linkStyle}>
                info@techlab4kids.it
              </a>
            }
          />
          <InfoRow
            label="PEC"
            value={
              <a href="mailto:techlab4kids@pec.it" style={linkStyle}>
                techlab4kids@pec.it
              </a>
            }
          />
        </dl>
      </LegalCard>

      <LegalCard title="Registro Imprese">
        <dl style={{ margin: 0 }}>
          <InfoRow label="Numero REA" value="CA — 360800" />
          <InfoRow label="Codice fiscale e numero di iscrizione" value="90043090928" />
          <InfoRow label="Data di iscrizione" value="02/10/2023" />
          <InfoRow
            label="Sezioni di iscrizione"
            value={
              <ul style={{ margin: 0, paddingLeft: "var(--tl4k-spacing-lg, 16px)" }}>
                <li>Iscritta nella sezione ORDINARIA</li>
                <li>
                  Iscritta nell&apos;apposita sezione speciale in qualità di{" "}
                  <strong>IMPRESA SOCIALE</strong>
                </li>
                <li>Iscrizione della titolarità effettiva nella sezione autonoma</li>
              </ul>
            }
          />
        </dl>
      </LegalCard>

      <LegalCard title="RUNTS — Registro Unico Nazionale del Terzo Settore">
        <dl style={{ margin: 0 }}>
          <InfoRow label="Repertorio" value="123832" />
          <InfoRow label="Data di iscrizione" value="02/10/2023" />
        </dl>
      </LegalCard>

      <LegalCard title="Attività">
        <dl style={{ margin: 0 }}>
          <InfoRow label="Data inizio attività" value="04/10/2023" />
          <InfoRow
            label="Attività prevalente"
            value="Altri servizi di istruzione n.c.a."
          />
          <InfoRow
            label="Ulteriori specifiche"
            value="Laboratori per il contrasto alla povertà educativa e alla dispersione scolastica"
          />
          <InfoRow label="Codice ATECO" value="85.59.9" />
          <InfoRow label="Codice NACE" value="85.59" />
        </dl>
      </LegalCard>

      <LegalCard title="Amministrazione">
        <dl style={{ margin: 0 }}>
          <InfoRow
            label="Sistema di amministrazione"
            value="Amministratore unico (in carica)"
          />
          <InfoRow
            label="Amministratore unico"
            value={
              <>
                Giancarlo Orrù{" "}
                <span style={{ color: "var(--tl4k-neutral-600, #6b6a6a)" }}>
                  (CF RROGCR71L05B745E)
                </span>
              </>
            }
          />
        </dl>
      </LegalCard>

      <p style={noteStyle}>
        Sito web:{" "}
        <a
          href="https://techlab4kids.it"
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
        >
          techlab4kids.it
        </a>
      </p>
    </>
  );
}

/* ─────────────────────────── Privacy ─────────────────────────── */

function PolicySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <LegalCard title={title}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--tl4k-spacing-sm, 8px)" }}>
        {children}
      </div>
    </LegalCard>
  );
}

export function PrivacyLegalContent() {
  return (
    <>
      <p style={bodyStyle}>
        <strong>TechLAB4Kids S.r.l. Impresa Sociale</strong> si impegna a proteggere la
        privacy delle bambine e dei bambini, delle famiglie e dei docenti che utilizzano
        le proprie applicazioni. La presente informativa descrive come trattiamo i dati,
        in conformità al <strong>Regolamento (UE) 2016/679 (GDPR)</strong> e alla
        normativa applicabile.
      </p>

      <PolicySection title="Titolare del trattamento">
        <p style={bodyStyle}>
          TechLAB4Kids S.r.l. Impresa Sociale (dati identificativi nella pagina{" "}
          <strong>La Società</strong> del footer).
        </p>
        <p style={bodyStyle}>
          E-mail:{" "}
          <a href="mailto:info@techlab4kids.it" style={linkStyle}>
            info@techlab4kids.it
          </a>{" "}
          · PEC:{" "}
          <a href="mailto:techlab4kids@pec.it" style={linkStyle}>
            techlab4kids@pec.it
          </a>
        </p>
      </PolicySection>

      <PolicySection title="Dati trattati e finalità">
        <p style={bodyStyle}>
          I dati raccolti sono utilizzati esclusivamente per erogare i servizi educativi
          (sessioni collaborative, conservazione degli artefatti, gestione dei profili
          insegnante). Operiamo nel rispetto del principio di{" "}
          <strong>minimizzazione</strong> (art. 5 GDPR): gli alunni usano{" "}
          <strong>pseudonimi</strong> e non richiediamo dati personali identificativi ai
          minori. Non effettuiamo profilazione né tracciamento a fini commerciali.
        </p>
      </PolicySection>

      <PolicySection title="Base giuridica">
        <p style={bodyStyle}>
          Il trattamento si fonda sull&apos;esecuzione di un compito di interesse pubblico
          o connesso all&apos;esercizio di pubblici poteri (art. 6, par. 1, lett. e, GDPR)
          nell&apos;ambito dell&apos;istruzione, nonché — per gli insegnanti — sul consenso
          (art. 6, par. 1, lett. a, GDPR). Per i servizi rivolti a minori di 14 anni il
          consenso, ove richiesto, è prestato dai genitori o da chi ne esercita la
          responsabilità genitoriale (art. 8 GDPR).
        </p>
      </PolicySection>

      <PolicySection title="Sistemi di intelligenza artificiale (AI Act, art. 50)">
        <p style={bodyStyle}>
          Alcune funzioni delle nostre applicazioni (es. suggerimenti di scrittura,
          generazione di domande, assistente in TechyWeb) si avvalgono di sistemi di{" "}
          <strong>intelligenza artificiale</strong>. In conformità all&apos;art. 50 del
          Regolamento (UE) 2024/1689 (AI Act):
        </p>
        <ul style={{ margin: 0, paddingLeft: "var(--tl4k-spacing-lg, 16px)" }}>
          <li>l&apos;utente è informato di interagire con un sistema di IA alla prima interazione;</li>
          <li>i contenuti generati dall&apos;IA sono marcati come tali (badge «Generato con IA»);</li>
          <li>
            i dati dei minori sono trattati con minimizzazione: inferenza locale nel
            browser o proxy server-side con chiave non esposta al client.
          </li>
        </ul>
      </PolicySection>

      <PolicySection title="Cookie Policy">
        <p style={bodyStyle}>
          Le nostre applicazioni utilizzano <strong>solo cookie tecnici</strong> (o
          equivalenti tecnologie di archiviazione locale) strettamente necessari al
          funzionamento della sessione e alle preferenze di accessibilità, ai sensi
          dell&apos;art. 122 del D.Lgs. 196/2003 e del Provvedimento Garante n. 229/2021.{" "}
          <strong>Non vengono utilizzati cookie di profilazione di terze parti.</strong>
        </p>
      </PolicySection>

      <PolicySection title="Conservazione e sicurezza">
        <p style={bodyStyle}>
          I dati sono conservati solo per il tempo necessario alle finalità e nel rispetto
          della normativa di settore. Adottiamo misure tecniche e organizzative adeguate
          a garantire la sicurezza (art. 32 GDPR) e la riservatezza dei dati.
        </p>
      </PolicySection>

      <PolicySection title="Diritti dell'interessato">
        <p style={bodyStyle}>
          Hai il diritto di chiedere l&apos;accesso, la rettifica, la cancellazione, la
          limitazione del trattamento, la portabilità dei dati e di opporti al trattamento
          (artt. 15-22 GDPR), scrivendo a{" "}
          <a href="mailto:info@techlab4kids.it" style={linkStyle}>
            info@techlab4kids.it
          </a>
          . Hai inoltre il diritto di proporre reclamo al{" "}
          <a
            href="https://www.garanteprivacy.it"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            Garante per la protezione dei dati personali
          </a>
          .
        </p>
      </PolicySection>

      <p style={noteStyle}>Informativa aggiornata: {LEGAL_PAGES_UPDATED_AT}</p>
    </>
  );
}

/* ─────────────────────── Accessibilità + matrice ─────────────────────── */

type StatusCell = "yes" | "partial" | "no" | "n/a";

export interface A11yRow {
  app: string;
  /** Design token / ThemeProvider presenti. */
  tokens: StatusCell;
  /** Footer normativo con link a pagine legali. */
  footer: StatusCell;
  /** Scan WCAG automatizzato (axe/pa11y) nel repository. */
  axe: StatusCell;
  /** Navigazione da tastiera dichiarata. */
  keyboard: StatusCell;
  /** Supporto screen reader dichiarato. */
  screenReader: StatusCell;
  /** Contrasto WCAG AA dichiarato. */
  contrast: StatusCell;
  /** Font ad alta leggibilità / ridimensionamento testo. */
  text: StatusCell;
  note?: string;
}

const SYMBOL: Record<StatusCell, string> = {
  yes: "✅",
  partial: "🟡",
  no: "❌",
  "n/a": "—",
};

const STATUS_LABEL: Record<StatusCell, string> = {
  yes: "Implementato",
  partial: "Parziale / in verifica",
  no: "Assente",
  "n/a": "Non applicabile",
};

/**
 * Matrice di conformità accessibilità per app (revisione 12/08/2026).
 *
 * Base di evidenza: presenza nel codice di ThemeProvider (design token),
 * TL4KAppFooter (footer normativo), test automatizzati axe/pa11y, e
 * funzionalità a11y specifiche. Gli stati "Parziale / in verifica"
 * indicano dichiarazioni non ancora coperte da verifica sistematica.
 *
 * Le app non-React (canvas/HTML statici) usano un footer legale statico
 * condiviso (`tl4k-legal-footer.html` via iframe) e non dispongono di
 * design token: per esse la colonna "Design token" resta ❌.
 */
export const TL4K_ACCESSIBILITY_MATRIX: A11yRow[] = [
  {
    app: "TechyWeb",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "yes",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Scan axe e2e attivo con eccezioni; le app interne dell'ambiente desktop non sono tutte auditate.",
  },
  {
    app: "Scrivi",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "yes",
    note: "Toolbar accessibilità: font, coppie contrasto, tutto maiuscole.",
  },
  {
    app: "Calcola",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Ausili visivi (abaco, regoli, monetine) con coerenza comandi.",
  },
  {
    app: "Disegna",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Armonia Numeri",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Riscontri",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "ScratchJr",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "no",
    contrast: "partial",
    text: "partial",
    note: "Test axe presenti; il core editor ScratchJr non è accessibile via screen reader.",
  },
  {
    app: "TechyBlocks (PenguinMod)",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Editor a blocchi ereditato da PenguinMod/Scratch.",
  },
  {
    app: "Vibra",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "QNS",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "SVG-Edit",
    tokens: "yes",
    footer: "yes",
    axe: "no",
    note: "Scan axe automatizzato in errore (stack overflow dell'engine, pre-esistente): verifica manuale richiesta.",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Spirografo",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Puzzle App",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Scacchi Jr",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Parola nel Mondo",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Cartoline",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Attività Interattive",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Anonimizza",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Include PrivacyAudit come strumento.",
  },
  {
    app: "Camera Sim",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Story Dice",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "AR Editor / AR Viewer",
    tokens: "yes",
    footer: "yes",
    axe: "no",
    note: "Non coperto dallo scan automatizzato: verifica manuale.",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "IoT Configurator",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Strumento tecnico.",
  },
  {
    app: "Kiosk Server UI",
    tokens: "yes",
    footer: "yes",
    axe: "no",
    note: "Pannello di servizio (insegnante/tecnico); non coperto dallo scan automatizzato.",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Teacher Presentation",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Registro presenze",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Techy Logo",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Mo-Mappe",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "App legacy: token e footer presenti, riqualificazione UI in corso.",
  },
  {
    app: "WordCloud (Nuvole di Parole)",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "App legacy: token e footer presenti, riqualificazione UI in corso.",
  },
  {
    app: "Magic Page",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Il mio libro (TL4K Book Creator)",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Admin Console",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Strumento interno di amministrazione (non rivolto agli alunni).",
  },
  {
    app: "Admin Dashboard",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "Strumento interno di amministrazione (non rivolto agli alunni).",
  },
  {
    app: "Parole Importanti",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Che Ora È",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Code Emotions",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Ingranaggi",
    tokens: "no",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "App canvas (non React): footer legale statico via iframe.",
  },
  {
    app: "KTuberling",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
  {
    app: "Pixel Art",
    tokens: "no",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
    note: "App vanilla JS (non React): footer legale statico via iframe.",
  },
  {
    app: "TL4K Mouse",
    tokens: "yes",
    footer: "yes",
    axe: "yes",
    keyboard: "partial",
    screenReader: "partial",
    contrast: "partial",
    text: "partial",
  },
];

function StatusCellView({ value }: { value: StatusCell }) {
  return (
    <span
      role="img"
      aria-label={STATUS_LABEL[value]}
      title={STATUS_LABEL[value]}
      style={{ cursor: "default" }}
    >
      {SYMBOL[value]}
    </span>
  );
}

function AccessibilityMatrixTable() {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "var(--tl4k-font-size-small, 18px)",
        }}
      >
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: "left", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>App</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Design token</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Footer legale</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Scan WCAG</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Tastiera</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Screen reader</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Contrasto AA</th>
            <th scope="col" style={{ textAlign: "center", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Font / testo</th>
            <th scope="col" style={{ textAlign: "left", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)" }}>Note</th>
          </tr>
        </thead>
        <tbody>
          {TL4K_ACCESSIBILITY_MATRIX.map((row) => (
            <tr key={row.app}>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", fontWeight: 600 }}>{row.app}</td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.tokens} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.footer} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.axe} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.keyboard} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.screenReader} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.contrast} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", textAlign: "center" }}><StatusCellView value={row.text} /></td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", fontSize: "var(--tl4k-font-size-small, 18px)", color: "var(--tl4k-neutral-600, #6b6a6a)" }}>{row.note ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AccessibilityLegalContent() {
  return (
    <>
      <p style={bodyStyle}>
        <strong>TechLAB4Kids S.r.l. Impresa Sociale</strong> si impegna a rendere le
        proprie applicazioni web accessibili a tutte le persone, indipendentemente da
        abilità, età o strumento di accesso, in conformità con:
      </p>
      <ul style={{ margin: 0, paddingLeft: "var(--tl4k-spacing-lg, 16px)" }}>
        <li>la <strong>Legge 9 gennaio 2004, n. 4</strong> e s.m.i. («Legge Stanca»);</li>
        <li>le <strong>WCAG 2.2 livello AA</strong> del W3C;</li>
        <li>la <strong>Direttiva (UE) 2019/882</strong> (European Accessibility Act), recepita dal D.Lgs. 82/2022;</li>
        <li>il <strong>DM 30 aprile 2025, n. 166</strong> (Linee guida IA e accessibilità per la scuola).</li>
      </ul>

      <LegalCard title="Stato di conformità per applicazione">
        <p style={bodyStyle}>
          Le piattaforme sono progettate seguendo le <strong>WCAG 2.2 livello AA</strong>{" "}
          con design system per fascia d&apos;età. La tabella seguente riporta, per
          ciascuna applicazione TL4K (inclusa TechyWeb), lo <strong>stato effettivo</strong>{" "}
          delle dichiarazioni di accessibilità, indicando esplicitamente le voci non
          ancora implementate o non coperte da verifica sistematica.
        </p>
        <div
          style={{
            margin: "var(--tl4k-spacing-md, 12px) 0",
            padding: "var(--tl4k-spacing-sm, 8px) var(--tl4k-spacing-md, 12px)",
            backgroundColor: "var(--tl4k-neutral-100, #f5f5f5)",
            borderRadius: "var(--tl4k-border-radius-md, 10px)",
            fontSize: "var(--tl4k-font-size-small, 18px)",
          }}
        >
          <strong>Legenda:</strong> ✅ Implementato · 🟡 Parziale / in verifica · ❌ Assente · — Non applicabile
        </div>
        <AccessibilityMatrixTable />
        <p style={{ ...noteStyle, marginTop: "var(--tl4k-spacing-md, 12px)" }}>
          Sono esclusi dalla matrice gli strumenti interni di infrastruttura e di
          test (es. deploy-dashboard, test-presentation-viewer) e il sito
          istituzionale techlab4kids.it, che non fanno parte del parco applicativo
          didattico e sono gestiti con procedure separate.
        </p>
      </LegalCard>

      <LegalCard title="Funzionalità di accessibilità disponibili">
        <ul style={{ margin: 0, paddingLeft: "var(--tl4k-spacing-lg, 16px)" }}>
          <li>Navigazione completa da <strong>tastiera</strong> e indicatori di focus sempre visibili;</li>
          <li>Supporto a <strong>screen reader</strong> (etichette ARIA, ruoli e alternative testuali);</li>
          <li>Contrasto cromatico conforme <strong>WCAG AA</strong> e palette ad alta leggibilità;</li>
          <li>Font progettati per la leggibilità dei bambini (es. <em>Lexend</em>, <em>OpenDyslexic</em>);</li>
          <li>Modalità <strong>tutto maiuscole</strong> e preferenze di lettura salvate nella sessione;</li>
          <li>Ridimensionamento del testo senza perdita di funzionalità;</li>
          <li>Comandi e pulsanti in posizioni coerenti tra le schermate (coerenza visiva BES).</li>
        </ul>
        <p style={{ ...noteStyle, marginTop: "var(--tl4k-spacing-md, 12px)" }}>
          Nota di trasparenza: le voci indicate come «Parziale / in verifica» riflettono
          dichiarazioni di progettazione non ancora coperte da verifica sistematica su
          tutte le schermate di ogni app.
        </p>
      </LegalCard>

      <LegalCard title="Segnalazione di problemi di accessibilità">
        <p style={bodyStyle}>
          Se riscontri un problema di accessibilità o desideri suggerire miglioramenti,
          contattaci all&apos;indirizzo{" "}
          <a href="mailto:info@techlab4kids.it" style={linkStyle}>
            info@techlab4kids.it
          </a>
          . Ti risponderemo entro 30 giorni lavorativi.
        </p>
      </LegalCard>

      <p style={noteStyle}>
        Dichiarazione pubblicata: {LEGAL_PAGES_UPDATED_AT} · Ultimo aggiornamento: {LEGAL_PAGES_UPDATED_AT}
      </p>
    </>
  );
}

/* ─────────────────────── Open Source e trasparenza ─────────────────────── */

interface LicenseEntry {
  project: string;
  license: string;
  url: string;
  note?: string;
}

const INTEGRATED_PROJECTS: LicenseEntry[] = [
  {
    project: "TechyWeb (daedalOS)",
    license: "MIT",
    url: "https://github.com/DustinBrett/daedalOS",
    note: "Desktop environment alla base di TechyWeb. Copyright (c) 2025 Dustin Brett.",
  },
  {
    project: "ScratchJr (scratch-jr-web)",
    license: "BSD-3-Clause",
    url: "https://github.com/scratchjr/scratchjr",
    note: "Copyright (c) 2016, Massachusetts Institute of Technology.",
  },
  {
    project: "TechyBlocks — PenguinMod (GUI / VM / Blocks / Render)",
    license: "GPL-3.0",
    url: "https://github.com/PenguinMod/PenguinMod-GUI",
    note: "Fork di TurboWarp/Scratch; le copie vendered includono il file LICENSE GPL-3.0.",
  },
  {
    project: "SVG-Edit",
    license: "MIT",
    url: "https://github.com/SVG-Edit/svgedit",
    note: "Bundle con sottolicenze Apache-2.0, LGPL-3.0-or-later e X11 (vedi licenseInfo.json).",
  },
  {
    project: "Character Creator (Code-Emotions)",
    license: "AGPL-3.0 + CC BY-SA 3.0",
    url: "https://github.com/ubik23/charactercreator",
    note: "Codice AGPL-3.0, artwork CC BY-SA 3.0. Copyright (C) 2016 Frédéric Guimont.",
  },
  {
    project: "WebLLM (AI locale nel browser)",
    license: "Apache-2.0",
    url: "https://github.com/mlc-ai/web-llm",
    note: "Inferenza LLM locale via WebGPU: nessun dato lascia il dispositivo.",
  },
  {
    project: "SheetJS (xlsx)",
    license: "Apache-2.0",
    url: "https://github.com/SheetJS/sheetjs",
  },
  {
    project: "ffmpeg.wasm",
    license: "MIT",
    url: "https://github.com/ffmpegwasm/ffmpeg.wasm",
    note: "FFmpeg sottostante: LGPL-2.1+ / GPL-2.0+ in base alla configurazione di build.",
  },
  {
    project: "BrowserFS",
    license: "Apache-2.0",
    url: "https://github.com/jvilk/BrowserFS",
  },
];

const MAIN_LIBRARIES: LicenseEntry[] = [
  { project: "React / ReactDOM", license: "MIT", url: "https://github.com/facebook/react" },
  { project: "React Router", license: "MIT", url: "https://github.com/remix-run/react-router" },
  { project: "Socket.IO client", license: "MIT", url: "https://github.com/socketio/socket.io-client" },
  { project: "Styled Components", license: "MIT", url: "https://github.com/styled-components/styled-components" },
  { project: "Tailwind CSS", license: "MIT", url: "https://github.com/tailwindlabs/tailwindcss" },
  { project: "Vite", license: "MIT", url: "https://github.com/vitejs/vite" },
  { project: "DOMPurify", license: "MPL-2.0", url: "https://github.com/cure53/DOMPurify" },
  { project: "lucide-react", license: "ISC", url: "https://github.com/lucide-icons/lucide" },
  { project: "marked", license: "MIT", url: "https://github.com/markedjs/marked" },
  { project: "i18next", license: "MIT", url: "https://github.com/i18next/i18next" },
  { project: "opentype.js", license: "MIT", url: "https://github.com/opentypejs/opentype.js" },
  { project: "isomorphic-git", license: "MIT", url: "https://github.com/isomorphic-git/isomorphic-git" },
  { project: "quickjs-emscripten", license: "MIT", url: "https://github.com/justjake/quickjs-emscripten" },
  { project: "canvas-confetti", license: "MIT", url: "https://github.com/catdad/canvas-confetti" },
  { project: "fflate", license: "MIT", url: "https://github.com/101arrowz/fflate" },
  { project: "motion", license: "MIT", url: "https://github.com/motiondivision/motion" },
  { project: "gif.js", license: "MIT", url: "https://github.com/jnordberg/gif.js" },
  { project: "three.js", license: "MIT", url: "https://github.com/mrdoob/three.js" },
  { project: "libheif", license: "LGPL-3.0", url: "https://github.com/strukturag/libheif" },
  { project: "html-to-image", license: "MIT", url: "https://github.com/bubkoo/html-to-image" },
];

function LicenseTable({ entries }: { entries: LicenseEntry[] }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "var(--tl4k-font-size-body, 20px)",
        }}
      >
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: "left", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)", color: "var(--tl4k-primary-700, #005a80)" }}>Progetto</th>
            <th scope="col" style={{ textAlign: "left", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)", color: "var(--tl4k-primary-700, #005a80)" }}>Licenza</th>
            <th scope="col" style={{ textAlign: "left", padding: 8, borderBottom: "2px solid var(--tl4k-primary-500, #0076a8)", color: "var(--tl4k-primary-700, #005a80)" }}>Repository</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.project}>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", verticalAlign: "top" }}>
                <strong>{entry.project}</strong>
                {entry.note && (
                  <span style={{ display: "block", fontSize: "var(--tl4k-font-size-small, 18px)", color: "var(--tl4k-neutral-600, #6b6a6a)" }}>
                    {entry.note}
                  </span>
                )}
              </td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", verticalAlign: "top", whiteSpace: "nowrap" }}>
                {entry.license}
              </td>
              <td style={{ padding: "8px 6px", borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)", verticalAlign: "top" }}>
                <a href={entry.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  GitHub ↗
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OpenSourceLegalContent() {
  return (
    <>
      <section
        className="tl4k-legal-content"
        style={{
          backgroundColor: "var(--tl4k-secondary-50, #fff6e5)",
          border: "2px solid var(--tl4k-secondary-500, #f39200)",
          borderRadius: "var(--tl4k-border-radius-lg, 16px)",
          padding: "var(--tl4k-spacing-lg, 16px) var(--tl4k-spacing-xl, 24px)",
        }}
      >
        <h2 style={{ ...headingStyle, color: "var(--tl4k-secondary-700, #b36a00)" }}>
          <span aria-hidden="true">🤖 </span>
          Codice generato con l&apos;assistenza dell&apos;IA
        </h2>
        <p style={bodyStyle}>
          Il codice aggiunto a questo progetto — oltre ai componenti open source
          originali elencati di seguito — è <strong>generato con l&apos;assistenza di
          sistemi di Intelligenza Artificiale</strong>, utilizzando principalmente, ma
          non solo: <strong>Claude</strong> (Anthropic), <strong>GPT</strong> (OpenAI),
          <strong> DeepSeek</strong>, <strong>Gemini</strong> (Google) e{" "}
          <strong>Qwen</strong> (Alibaba).
        </p>
        <p style={{ ...bodyStyle, marginTop: "var(--tl4k-spacing-md, 12px)" }}>
          Il codice generato dall&apos;IA è sempre <strong>revisionato e verificato da
          persone</strong> prima della pubblicazione. La generazione assistita non
          modifica le licenze del codice originale: i diritti e gli obblighi previsti
          dalle licenze indicate in questa pagina restano invariati.
        </p>
      </section>

      <LegalCard title="Applicazioni e componenti open source integrati">
        <p style={{ ...bodyStyle, margin: "0 0 var(--tl4k-spacing-md, 12px)" }}>
          Progetti open source integrati nel nostro ambiente didattico, con le rispettive
          licenze verificate sui repository ufficiali e sui file LICENSE inclusi nel codice.
        </p>
        <LicenseTable entries={INTEGRATED_PROJECTS} />
      </LegalCard>

      <LegalCard title="Librerie principali">
        <p style={{ ...bodyStyle, margin: "0 0 var(--tl4k-spacing-md, 12px)" }}>
          Principali dipendenze JavaScript delle applicazioni. L&apos;elenco completo e
          puntuale di ogni pacchetto è disponibile nei file <code>package.json</code> e
          nei lockfile di ciascuna applicazione.
        </p>
        <LicenseTable entries={MAIN_LIBRARIES} />
      </LegalCard>

      <LegalCard title="Verifica delle clausole di licenza">
        <ul style={{ margin: 0, paddingLeft: "var(--tl4k-spacing-lg, 16px)" }}>
          <li>
            <strong>MIT / BSD-3-Clause / ISC</strong>: obbligo di conservare i copyright
            notice nelle redistribuzioni → notice presenti nei file <code>LICENSE</code> del
            repository. ✅
          </li>
          <li>
            <strong>Apache-2.0</strong> (WebLLM, SheetJS, BrowserFS): obbligo di notice e
            indicazione delle modifiche locali → notice conservate; le modifiche al codice
            vendered sono versionate nel monorepo. ✅
          </li>
          <li>
            <strong>GPL-3.0</strong> (PenguinMod / TechyBlocks): ogni modifica distribuita
            deve essere rilasciata sotto GPL-3.0 e deve essere disponibile il
            «Corresponding Source» → il sorgente modificato è pubblicato nel repository del
            progetto (monorepo open source) e i file mantengono le notice GPL. ✅
          </li>
          <li>
            <strong>AGPL-3.0</strong> (Character Creator / Code-Emotions): anche l&apos;uso
            tramite rete richiede di offrire il sorgente → repository pubblico del progetto.
            ✅ Gli <strong>artwork CC BY-SA 3.0</strong> richiedono attribuzione e
            condivisione con la stessa licenza → attribuzione presente nel file LICENSE.
          </li>
          <li>
            <strong>LGPL-3.0</strong> (libheif): linking di libreria consentito mantenendo le
            notice → ✅
          </li>
        </ul>
        <p style={{ ...bodyStyle, marginTop: "var(--tl4k-spacing-md, 12px)" }}>
          Nota: il progetto è distribuito come applicazione web e il codice sorgente del
          monorepo è pubblico. Per qualsiasi dubbio sulle licenze contattaci:{" "}
          <a href="mailto:info@techlab4kids.it" style={linkStyle}>
            info@techlab4kids.it
          </a>
          .
        </p>
      </LegalCard>

      <p style={noteStyle}>Pagina aggiornata: {LEGAL_PAGES_UPDATED_AT}</p>
    </>
  );
}
